/**
 * SceneCamera — render the live camera feed as a scene-level background.
 *
 * The DOM `<video>` element owned by `CameraPassthrough` is hidden. This
 * helper reads its frames on every tick, draws them into a backing
 * `<canvas>`, registers that canvas as a Phaser texture, and mounts it
 * as an image at depth -100 (behind everything else in the scene).
 *
 * Why draw-to-canvas instead of `textures.addVideo(videoElement)`:
 *   - Reliable across every Phaser 3.x version (no feature-detection)
 *   - Clean mirror via canvas transform, independent of CSS
 *   - Explicit per-frame refresh — we control when the texture updates
 *
 * Cleanup is wired to `scene.events.on('shutdown')` so when the scene
 * stops (match ends, quit to menu), the Phaser texture is removed and
 * the per-frame draw loop stops. The `<video>` element itself stays
 * alive for the next scene that wants it.
 */
export class SceneCamera {
  /**
   * Attach the camera feed to a scene as a cover-scaled background.
   *
   * @param {Phaser.Scene} scene
   * @param {object} [opts]
   * @param {HTMLVideoElement} [opts.video] - override (defaults to sdk.camera.getVideoElement())
   * @param {boolean} [opts.mirror] - override (defaults to sdk.camera.mirror)
   * @param {number} [opts.depth] - depth for the background image (default -100)
   * @returns {{ image: Phaser.GameObjects.Image, destroy: () => void } | null}
   */
  static attach(scene, opts = {}) {
    if (!scene || typeof document === 'undefined') return null;

    // Resolve the video element. Prefer an explicit opts.video, otherwise
    // reach into the SDK registry for the CameraPassthrough instance.
    let video = opts.video || null;
    let mirror = opts.mirror;
    if (!video) {
      const passthrough = scene.registry && scene.registry.get('camera');
      if (passthrough && typeof passthrough.getVideoElement === 'function') {
        video = passthrough.getVideoElement();
        if (mirror == null) mirror = !!passthrough.mirror;
      }
    }
    if (!video) return null;
    if (mirror == null) mirror = false;

    // Wait for the first frame so we know the texture dimensions.
    // If the video isn't ready yet, schedule a retry on the next frame.
    if (!video.videoWidth || !video.videoHeight) {
      let cancelled = false;
      const retry = () => {
        if (cancelled) return;
        if (video.videoWidth && video.videoHeight) {
          SceneCamera.attach(scene, opts);
        } else {
          scene.time.delayedCall(100, retry);
        }
      };
      scene.time.delayedCall(100, retry);
      return {
        image: null,
        destroy() { cancelled = true; }
      };
    }

    const vw = video.videoWidth;
    const vh = video.videoHeight;

    // Backing canvas that mirrors the video frame-by-frame.
    const canvas = document.createElement('canvas');
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext('2d');

    // Unique texture key so multiple scenes can attach independently.
    const key = '__sdk_camera_' + scene.scene.key + '_' + Date.now();

    // Remove a stale texture with the same key (e.g. if a previous
    // shutdown didn't run). Guarded because Phaser throws on missing.
    if (scene.textures.exists(key)) scene.textures.remove(key);
    const phaserTexture = scene.textures.addCanvas(key, canvas);

    // Draw the first frame synchronously so the image has visible content.
    drawFrame();

    const W = scene.scale.width;
    const H = scene.scale.height;
    const image = scene.add.image(W / 2, H / 2, key);
    image.setDepth(opts.depth == null ? -100 : opts.depth);
    image.setScrollFactor(0);

    // Cover-scale the image to fill the Phaser canvas regardless of the
    // video's native aspect ratio.
    const scale = Math.max(W / vw, H / vh);
    image.setScale(scale);

    if (mirror) image.setFlipX(true);

    // Per-frame texture refresh so the image shows live video.
    const onUpdate = () => {
      if (!video || !video.videoWidth) return;
      drawFrame();
      if (phaserTexture && phaserTexture.refresh) phaserTexture.refresh();
    };
    scene.events.on('update', onUpdate);

    // Cleanup on scene shutdown.
    const destroy = () => {
      scene.events.off('update', onUpdate);
      if (image && image.destroy) image.destroy();
      if (scene.textures.exists(key)) scene.textures.remove(key);
    };
    scene.events.once('shutdown', destroy);
    scene.events.once('destroy', destroy);

    return { image, destroy };

    // ── helpers ────────────────────────────────────────────────
    function drawFrame() {
      try {
        // No mirror here — the Phaser image flips via setFlipX so the
        // canvas holds the raw, un-mirrored frames and other consumers
        // (like snapshot capture) get the original orientation.
        ctx.drawImage(video, 0, 0, vw, vh);
      } catch (_) {
        // A fresh stream may throw on the very first draw — ignore,
        // the next frame will succeed.
      }
    }
  }
}
